import type { Location } from '@/models/index';
import * as sphere from 'ol/sphere';
import type { RunCourseMtx } from '@/models/runCourse';

// 地図上の2点間の距離(m)を計算して返す.
export function getDistanceInMeters(start: Location, end: Location): number {
  const c1 = [start.lon, start.lat];
  const c2 = [end.lon, end.lat];
  return sphere.getDistance(c1, c2);
}

/**
 * https://www.movable-type.co.uk/scripts/latlong.html
 * を実装したopenlayersからパクってきた
 * https://github.com/openlayers/openlayers/blob/v5.2.0/src/ol/sphere.js
 */
export function toDegrees(angleInRadians: number): number {
  return angleInRadians * 180 / Math.PI;
}

export function toRadians(angleInDegrees: number): number {
  return angleInDegrees * Math.PI / 180;
}

// from https://github.com/kokudo-digital/land-movie-pf-ap/blob/develop/src/assets/src/lib/geoCalcHelper.ts#L19
export function getBearing(pt1: Location, pt2: Location): number {
  const lat1 = toRadians(pt1.lat);
  const lon1 = toRadians(pt1.lon);
  const lat2 = toRadians(pt2.lat);
  const lon2 = toRadians(pt2.lon);
  const y = Math.sin(lon2 - lon1) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) -
          Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);
  // -π〜π のラジアン（北が0・時計回り）
  return Math.atan2(y, x);
}

// 2点間の距離をハーバーサイン式で計算する
// https://www.movable-type.co.uk/scripts/latlong.html
export function haversineDistance(mtx1: RunCourseMtx, mtx2: RunCourseMtx): number {
  const { lat: lat1, lon: lon1 } = mtx1;
  const { lat: lat2, lon: lon2 } = mtx2;
  const R = 6371000;
  const toRadians = (deg: number): number => (deg * Math.PI) / 180;

  const lat1Rad = toRadians(lat1);
  const lat2Rad = toRadians(lat2);
  const deltaLat = toRadians(lat2 - lat1);
  const deltaLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) *
    Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
}
